import Papa from 'papaparse';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useInitialData } from 'contexts/InitialDataContext';
import { getTagEnglishName } from 'services/initialDataApi';
import type { ParsedIdea } from '../hooks/useBulkEpisodeOrchestrator';

interface BulkCsvUploadProps {
  onIdeasParsed: (ideas: ParsedIdea[]) => void;
  disabled?: boolean;
}

const REQUIRED_COLUMNS = ['skill_level', 'idea'] as const;

export function BulkCsvUpload({ onIdeasParsed, disabled }: BulkCsvUploadProps) {
  const { state: initialDataState } = useInitialData();

  // Build CEFR code -> ID lookup from DB skill levels (e.g. "A1" -> 1, "B2" -> 4)
  const skillLevelLookup = useMemo(() => {
    const lookup: Record<string, number> = {};
    for (const sl of initialDataState.data?.skillLevels ?? []) {
      lookup[sl.cefr_code.toUpperCase()] = sl.id;
      lookup[String(sl.id)] = sl.id;
    }
    return lookup;
  }, [initialDataState.data?.skillLevels]);

  // Build tag name -> ID lookup (case-insensitive)
  const tagNameLookup = useMemo(() => {
    const lookup: Record<string, number> = {};
    for (const tag of initialDataState.data?.tags ?? []) {
      lookup[getTagEnglishName(tag).toLowerCase()] = tag.id;
    }
    return lookup;
  }, [initialDataState.data?.tags]);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [stats, setStats] = useState<{ total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError('');
      setStats(null);

      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.name.endsWith('.csv')) {
        setError('Please upload a .csv file');
        return;
      }

      setFileName(file.name);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = results.meta.fields || [];

          // Validate required columns
          const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
          if (missing.length > 0) {
            setError(`Missing required columns: ${missing.join(', ')}`);
            return;
          }

          const rows = results.data as Record<string, string>[];
          if (rows.length === 0) {
            setError('CSV file has no data rows');
            return;
          }

          // Parse and validate ideas
          const validCodes = Object.keys(skillLevelLookup).join(', ');
          const ideas: ParsedIdea[] = [];
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rawLevel = row.skill_level?.trim().toUpperCase();
            const skillLevel = rawLevel ? skillLevelLookup[rawLevel] : undefined;
            if (!skillLevel) {
              setError(
                `Invalid skill_level "${row.skill_level}" at row ${i + 1}. Valid values: ${validCodes}`
              );
              return;
            }

            const tagNamesStr = row.tag_names?.trim();
            const tagIds = tagNamesStr
              ? tagNamesStr
                  .split(',')
                  .map((name) => tagNameLookup[name.trim().toLowerCase()])
                  .filter((id): id is number => id !== undefined)
              : undefined;

            ideas.push({
              index: i,
              idea: row.idea,
              skillLevel,
              duration: row.duration || undefined,
              title: row.title || undefined,
              tagIds: tagIds?.length ? tagIds : undefined,
              tagNames: tagNamesStr || undefined,
              learningGoalId: row.learning_goal_id ? parseInt(row.learning_goal_id, 10) : undefined,
              learningGoalName: row.learning_goal_name || undefined,
            });
          }

          setStats({ total: rows.length });
          onIdeasParsed(ideas);
        },
        error: (err) => {
          setError(`Failed to parse CSV: ${err.message}`);
        },
      });
    },
    [onIdeasParsed, skillLevelLookup, tagNameLookup]
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="mb-4 text-lg font-medium text-gray-900">Step 1: Upload Episode Ideas CSV</h3>

      <div className="flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          disabled={disabled}
          className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
        />
      </div>

      {fileName && stats && <p className="mt-3 text-sm text-gray-600">{stats.total} ideas found</p>}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}
